import React, { useState } from "react";
import { SimpleSwitcher } from "./SimpleSwitcher";
import { ConsentService } from "./CookieConsentModal";

interface CookieCategoryProps {
    title: string;
    description: string;
    services: ConsentService[];
    allServicesText: string;
    showMoreText: string;
    serviceConsents: Record<string, boolean>;
    onConsentChange: (serviceId: string, isEnabled: boolean) => void;
}

export const CookieCategory: React.FC<CookieCategoryProps> = ({
    title,
    description,
    services,
    allServicesText,
    showMoreText,
    serviceConsents,
    onConsentChange
}) => {
    const [isExpanded, setIsExpanded] = useState(false); // Default to expanded as per markup
    // Replicating the original ToggleSwitch which had icons but no text labels.
    const switcherEntities = [
        {
            properties: {
                value: "off",
                icon: "tq-icon-close"
            }
        },
        {
            properties: {
                value: "on",
                icon: "tq-icon-check"
            }
        }
    ];

    // Determine if all services in this category are enabled
    const areAllServicesEnabled = services.every(service => serviceConsents[service.code]);

    // Handle toggling all services in the category
    const handleCategoryToggle = (newValue: string) => {
        const isEnabled = newValue === "on";
        services.forEach(service => {
            // Only call onConsentChange if the state is different to avoid unnecessary re-renders
            if (serviceConsents[service.code] !== isEnabled)
                onConsentChange(service.code, isEnabled);
        });
    };

    const handleToggle = () => setIsExpanded(!isExpanded);

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleToggle();
        }
    };

    return (
        <div className="tq-accordion un-ds-simple-accordion">
            <div className={`tq-section tq-expandable ${isExpanded ? "tq-expanded" : ""}`}>
                <div className="tq-section-header">
                    <div className="tq-splitbox">
                        <div className="tq-splitbox-primary">
                            <div className="un-accordion-header-left">
                                <div
                                    className="tq-title"
                                    role="button"
                                    aria-label={title}
                                    tabIndex={0}
                                    aria-expanded={isExpanded}
                                    onClick={handleToggle}
                                    onKeyDown={handleKeyDown}
                                >
                                    <span className="tq-text-node">{title}</span>
                                </div>
                                <span>{description}</span>
                            </div>
                            <div className="un-accordion-header-right">
                                <p>{allServicesText}</p>
                                <SimpleSwitcher
                                    entities={switcherEntities}
                                    value={areAllServicesEnabled ? "on" : "off"}
                                    onChange={handleCategoryToggle}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="tq-section-content">
                    {services.map(service => (
                        <div className="un-cookie-inner" key={service.title}>
                            <p className="un-ds-heading-5">{service.title}</p>
                            <div className="un-cookie-inner-content">
                                <p>{service.description}</p>
                                <a
                                    href={service.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="tq-hyperlink un-ds-hyperlink tq-main-color"
                                >
                                    <span className="tq-hyperlink-z-index">{showMoreText}</span>
                                </a>
                            </div>
                            <div className="un-cookie-inner-switch">
                                <SimpleSwitcher
                                    entities={switcherEntities}
                                    value={serviceConsents[service.code] ? "on" : "off"}
                                    onChange={() =>
                                        onConsentChange(
                                            service.code,
                                            !serviceConsents[service.code]
                                        )
                                    }
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
